/**
 * Membership & credits screen (DNKADM-0008 U2) — the sub-tab container that replaces
 * the 0006 `membership-credits` placeholder. Owner/Admin only (the route is already
 * wrapped in `RequireAdmin` by 0006; this screen does not re-gate).
 *
 * Hosts MUI sub-tabs **Plans · Packages · Credit buckets · Daily caps · Settings**,
 * routed at `membership-credits/plans|packages|credit-buckets|daily-caps|settings`
 * (index → plans). Packages (DNKADM-0102) manages one-time offerings (`kind=package`)
 * with by-bucket credit grants — the sibling of Plans; the nav item is "Plans &
 * Packages". The
 * active tab is derived from the URL and each tab navigates to its sub-path, so the tabs
 * are deep-linkable and back/forward works. The tab bodies render through an `<Outlet />`.
 * Per-plan credit deposits live inside the Plans tab (DNKADM-0056); the v1 tier-credit
 * matrix tab is retired (credits are now per-plan, per-bucket by id). Credit buckets and
 * Daily caps were folded in as tabs (DNKADM-0085 — formerly standalone screens, re-housed
 * unchanged; old `/credit-buckets` and `/daily-caps` paths redirect into these tabs).
 */
import { Box, Tab, Tabs, Typography } from '@mui/material';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { COURT_GREEN } from '@/theme';

/** The five sub-tabs in display/route order. `path` is the route-map sub-tab path. */
const SUB_TABS = [
  { path: 'plans', label: 'Plans' },
  { path: 'packages', label: 'Packages' },
  { path: 'credit-buckets', label: 'Credit buckets' },
  { path: 'daily-caps', label: 'Daily caps' },
  { path: 'settings', label: 'Plan Settings' },
] as const;

type SubTabPath = (typeof SUB_TABS)[number]['path'];

/**
 * Resolve the active sub-tab from the current URL. The container only renders under
 * `/membership-credits/<path>`; an unknown/trailing segment falls back to `plans`
 * (matching the index redirect) so a tab is always selected.
 */
function activeTabFrom(pathname: string): SubTabPath {
  const match = SUB_TABS.find(
    (t) => pathname === `/membership-credits/${t.path}` ||
      pathname.startsWith(`/membership-credits/${t.path}/`),
  );
  return match?.path ?? 'plans';
}

export function MembershipCreditsScreen() {
  const location = useLocation();
  const navigate = useNavigate();
  const active = activeTabFrom(location.pathname);

  return (
    <Box>
      <Typography variant="h1">Plans &amp; Packages</Typography>
      <Typography variant="body2" sx={{ mt: 0.5, mb: 2 }}>
        Define what members can buy and what it grants: the plans they subscribe to, the one-time
        packages they purchase, the credits each deposits per bucket, and how credits are valued
        and refunded.
      </Typography>

      <Tabs
        value={active}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        onChange={(_e, value: SubTabPath) => navigate(value)}
        aria-label="Plans & Packages sections"
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          '& .MuiTabs-indicator': { bgcolor: COURT_GREEN },
          '& .Mui-selected': { color: `${COURT_GREEN} !important` },
        }}
      >
        {SUB_TABS.map((t) => (
          <Tab key={t.path} value={t.path} label={t.label} />
        ))}
      </Tabs>

      <Box sx={{ pt: 2.5 }}>
        <Outlet />
      </Box>
    </Box>
  );
}
